/**
 * SettingsSidebar — T2 sidebar for the Settings module.
 *
 * Shows settings scopes:
 *  - Organization (always visible)
 *  - Platform (only if user has platform.settings.view permission)
 */
import React from 'react';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import Globe from 'lucide-react/dist/esm/icons/globe';
import type { LucideIcon } from 'lucide-react';
import { useUIStore } from '../../../stores/uiStore';
import type { SettingsScope } from '../../../stores/uiStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import { getTranslation } from '../../../i18n';

interface ScopeItem {
  id: SettingsScope;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  permission?: string;
}

const SCOPES: ScopeItem[] = [
  {
    id: 'organization',
    labelKey: 'settingsNavOrganization',
    descriptionKey: 'settingsNavOrganizationDesc',
    icon: Building2,
  },
  {
    id: 'platform',
    labelKey: 'settingsNavPlatform',
    descriptionKey: 'settingsNavPlatformDesc',
    icon: Globe,
    permission: Permissions.PLATFORM_SETTINGS_VIEW,
  },
];

const SettingsSidebar: React.FC = () => {
  const { settingsScope, setSettingsScope, language } = useUIStore();
  const { hasPermission } = usePermissions();
  const t = (key: string) => getTranslation(language, key);

  const visibleScopes = SCOPES.filter(
    (s) => !s.permission || hasPermission(s.permission as any),
  );

  return (
    <aside className="fixed top-[60px] left-[60px] w-[240px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-[52] flex flex-col transition-all duration-300">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-subtle">
        <h2 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
          {t('settingsSidebarTitle')}
        </h2>
      </div>

      {/* Scope Navigation */}
      <nav className="flex-1 px-2 py-2 space-y-0.5">
        {visibleScopes.map((scope) => {
          const isActive = settingsScope === scope.id;
          const Icon = scope.icon;

          return (
            <button
              key={scope.id}
              onClick={() => setSettingsScope(scope.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                transition-all duration-150 border-none outline-none cursor-pointer
                ${isActive
                  ? 'bg-status-success/10 text-status-success'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
                }
              `}
            >
              <div
                className={`
                  w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  ${isActive ? 'bg-status-success/15' : 'bg-surface-primary'}
                `}
              >
                <Icon
                  size={16}
                  strokeWidth={isActive ? 2.2 : 1.8}
                  className={isActive ? 'text-status-success' : 'text-content-muted'}
                />
              </div>
              <div className="min-w-0">
                <span className={`block text-sm font-medium ${isActive ? 'text-status-success' : 'text-content-secondary'}`}>
                  {t(scope.labelKey)}
                </span>
                <span className="block text-[11px] text-content-muted mt-0.5 truncate">
                  {t(scope.descriptionKey)}
                </span>
              </div>
              {isActive && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-status-success shrink-0" />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default React.memo(SettingsSidebar);
